import { useEffect, useMemo, useState } from "react";

function pickQueryParam(qs, keys) {
  for (let i = 0; i < keys.length; i++) {
    const v = qs.get(keys[i]);
    if (v) return v;
  }
  return null;
}

export default function PaymentReturn() {
  const [status, setStatus] = useState("checking"); // checking | pending | success | error
  const [message, setMessage] = useState("Initialisation…");
  const [details, setDetails] = useState(null);

  const functionsBase = useMemo(() => {
    // ✅ Si tu as un proxy/rewrites, tu peux mettre "" et utiliser /api/...
    return (
      process.env.REACT_APP_FUNCTIONS_BASE_URL ||
      "https://europe-west1-ghana-67644.cloudfunctions.net"
    );
  }, []);

  useEffect(() => {
    const qs = new URLSearchParams(window.location.search);

    // identifier peut s’appeler identifier / orderId / reference selon ton flow
    const identifier =
      pickQueryParam(qs, ["identifier", "orderId", "reference", "ref"]) || "";

    // PayGate peut renvoyer tx_reference sous plusieurs formes
    const tx_reference =
      pickQueryParam(qs, [
        "tx_reference",
        "TX_REFERENCE",
        "txReference",
        "txref",
        "transaction",
        "transaction_id",
        "ref",
      ]) || "";

    if (!identifier) {
      setStatus("error");
      setMessage("Identifiant de paiement manquant (identifier).");
      return;
    }

    const verify = async () => {
      try {
        setStatus("checking");
        setMessage("Vérification du paiement en cours…");

        // ✅ Endpoint (abonnement)
        const url = `${functionsBase}/verifyPaygateProSubscription`;

        // Si tx_reference est absent, on ne peut pas vérifier côté PayGate /status.
        // Donc on passe en "pending" (ou tu peux implémenter un endpoint qui check par identifier si PayGate le permet).
        if (!tx_reference) {
          setStatus("pending");
          setMessage(
            "Retour PayGate reçu, mais la référence (tx_reference) n’est pas fournie. " +
              "Si vous avez payé, contactez le support avec votre identifiant."
          );
          setDetails({ identifier });
          return;
        }

        const res = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ identifier, tx_reference }),
        });

        let data = {};
        try {
          data = await res.json();
        } catch {
          data = {};
        }

        if (!res.ok || !data.ok) {
          setStatus("error");
          setMessage(
            data?.error ||
              "Paiement non confirmé. Si vous avez payé, contactez le support."
          );
          setDetails({
            identifier,
            tx_reference,
            httpStatus: res.status,
            raw: data || null,
          });
          return;
        }

        setStatus("success");
        setMessage("Paiement confirmé ✅ Votre Pack Pro est maintenant actif.");
        setDetails({
          identifier,
          tx_reference,
          vendorId: data.vendorId || null,
          planId: data.planId || null,
        });
      } catch (e) {
        setStatus("error");
        setMessage("Erreur réseau. Réessayez ou contactez le support.");
        setDetails({ identifier, tx_reference, error: String(e?.message || e) });
      }
    };

    verify();
  }, [functionsBase]);

  return (
    <div className="container py-4" style={{ maxWidth: 720 }}>
      <h3 className="mb-2">Retour paiement</h3>

      {status === "checking" && <div className="alert alert-info">{message}</div>}
      {status === "pending" && <div className="alert alert-warning">{message}</div>}
      {status === "success" && <div className="alert alert-success">{message}</div>}
      {status === "error" && <div className="alert alert-danger">{message}</div>}

      {/* Détails utiles pour support (optionnel) */}
      {details && (
        <div className="card p-3 mb-3">
          <div className="fw-semibold mb-2">Informations</div>
          <div className="small text-muted">
            <div>
              <strong>identifier:</strong> {details.identifier || "-"}
            </div>
            <div>
              <strong>tx_reference:</strong> {details.tx_reference || "-"}
            </div>
            {details.httpStatus ? (
              <div>
                <strong>HTTP:</strong> {details.httpStatus}
              </div>
            ) : null}
          </div>
        </div>
      )}

      <div className="d-flex gap-2 flex-wrap">
        <a className="btn btn-outline-secondary" href="/vendor/abonnement">
          Retour à mon abonnement
        </a>

        {/* Bouton retry utile si réseau */}
        {(status === "error" || status === "pending") && (
          <button
            className="btn btn-primary"
            onClick={() => window.location.reload()}
            type="button"
          >
            Réessayer la vérification
          </button>
        )}
      </div>
    </div>
  );
}
